<template>
	<div class="filter-value-input">
		<!-- No value needed operators -->
		<template v-if="noValueNeeded">
			<TextField
				value="(No value required)"
				outlined
				small
				disabled
				readonly
				hide-details
			/>
		</template>

		<!-- Between operators -->
		<template v-else-if="isBetweenOperator">
			<div>
				<template v-if="isDateType">
					<v-menu
						ref="dateRangeMenu"
						:close-on-content-click="false"
						transition="scale-transition"
						offset-y
						min-width="auto"
					>
						<template #activator="{ on, attrs }">
							<TextField
								:value="dateRangeDisplayText"
								label="Select date range"
								append-icon="mdi-calendar-range"
								outlined
								small
								hide-details
								readonly
								v-bind="attrs"
								v-on="on"
							/>
						</template>
						<v-date-picker
							:value="dateRangeValue"
							range
							no-title
							@input="handleDateRangeChange"
						/>
					</v-menu>
				</template>
				<template v-else>
					<div class="d-flex flex-row align-center justify-space-between between-fields-container">
						<TextField
							:value="Array.isArray(value) ? value[0] : null"
							label="Minimum"
							:type="isNumericType ? 'number' : 'text'"
							outlined
							small
							hide-details
							@input="updateValue(0, $event)"
						/>

						<span class="mx-1 text--secondary">
							to
						</span>

						<TextField
							:value="Array.isArray(value) ? value[1] : null"
							label="Maximum"
							:type="isNumericType ? 'number' : 'text'"
							outlined
							small
							hide-details
							@input="updateValue(1, $event)"
						/>
					</div>
				</template>
			</div>
		</template>

		<!-- Multi-value operators (e.g., INCLUDES / DOES NOT INCLUDE) -->
		<template v-else-if="isMultiValueOperator">
			<div class="multi-editor d-flex flex-column align-start">
				<div class="d-flex align-end flex-nowrap w-100 multi-editor__input-row">
					<TextField
						v-model="newValue"
						label="Add a value"
						outlined
						small
						hide-details
						:disabled="disabled"
						class="value-input flex-grow-1 min-width-0"
						@keyup.enter="addOneValue"
					/>
					<v-btn
						small
						color="primary"
						class="ml-2"
						:disabled="disabled || !canAdd"
						@click="addOneValue"
					>
						Add
					</v-btn>
				</div>
				<div class="value-chips">
					<v-chip
						v-for="(chip, index) in multiValueArray"
						:key="index"
						small
						class="mr-1 mb-1"
						close
						@click:close="removeAt(index)"
					>
						{{ chip }}
					</v-chip>
				</div>
			</div>
		</template>

		<!-- Date types -->
		<template v-else-if="isDateType">
			<v-menu
				ref="dateMenu"
				:close-on-content-click="false"
				transition="scale-transition"
				offset-y
				min-width="auto"
			>
				<template #activator="{ on, attrs }">
					<TextField
						:value="value"
						label="Select date"
						append-icon="mdi-calendar"
						outlined
						small
						hide-details
						readonly
						v-bind="attrs"
						v-on="on"
					/>
				</template>
				<v-date-picker
					:value="value"
					no-title
					@input="$emit('input', $event); $refs.dateMenu.save($event)"
				/>
			</v-menu>
		</template>

		<!-- Boolean types -->
		<template v-else-if="isBooleanType">
			<Dropdown
				:value="value"
				:items="booleanOptions"
				label="Select value"
				outlined
				small
				hide-details
				@input="$emit('input', $event)"
			/>
		</template>

		<!-- Numeric types -->
		<template v-else-if="isNumericType">
			<TextField
				:value="value"
				label="Enter number"
				type="number"
				outlined
				small
				hide-details
				@input="$emit('input', $event)"
			/>
		</template>

		<!-- Default text input -->
		<template v-else>
			<TextField
				:value="value"
				label="Enter value"
				outlined
				small
				hide-details
				@input="$emit('input', $event)"
			/>
		</template>
	</div>
</template>

<script>
import { VDatePicker, VMenu, VBtn, VChip } from 'vuetify/lib/components'
import TextField from '../../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
export default {
	name: 'filter-value-input',
	components: {
		VDatePicker,
		VMenu,
		VBtn,
		VChip,
		TextField,
		Dropdown
	},
	props: {
		field: {
			type: Object,
			required: true
		},
		operator: {
			type: String,
			default: null
		},
		value: {
			type: [String, Number, Array],
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			newValue: ''
		}
	},
	computed: {
		noValueNeeded () {
			return ['IS NULL', 'IS NOT NULL', 'IS', 'IS NOT'].includes(this.operator)
		},
		isListOperator () {
			// NO FILTER SHOULD BE MULTILINE - all operators search within field values, not provide multiple search values
			return false
		},
		isBetweenOperator () {
			return this.operator === 'BETWEEN' || this.operator === 'NOT BETWEEN'
		},
		isMultiValueOperator () {
			const op = (this.operator || '').toString().trim().toLowerCase()
			return op === 'includes' || op === 'does not include' || op === 'in' || op === 'not in'
		},
		isDateType () {
			const dataType = this.field.typeFriendlyName || this.field.dataType || ''
			return ['datetime', 'date', 'time', 'timestamp'].includes(dataType.toLowerCase())
		},
		isBooleanType () {
			const dataType = this.field.typeFriendlyName || this.field.dataType || ''
			return ['bool', 'boolean'].includes(dataType.toLowerCase())
		},
		isNumericType () {
			const dataType = this.field.typeFriendlyName || this.field.dataType || ''
			return ['int', 'integer', 'float', 'double', 'decimal', 'number', 'numeric'].includes(dataType.toLowerCase())
		},
		booleanOptions () {
			return [
				{ text: 'True', value: '1' },
				{ text: 'False', value: '0' }
			]
		},
		dateRangeValue () {
			// Convert our array format to v-date-picker range format
			if (Array.isArray(this.value) && this.value.length >= 2) {
				return [this.value[0], this.value[1]].filter(Boolean)
			}
			return []
		},
		dateRangeDisplayText () {
			if (Array.isArray(this.value) && this.value[0] && this.value[1]) {
				const startDate = this.formatDateForDisplay(this.value[0])
				const endDate = this.formatDateForDisplay(this.value[1])
				return `${startDate} - ${endDate}`
			} else if (Array.isArray(this.value) && this.value[0]) {
				return `From ${this.formatDateForDisplay(this.value[0])}`
			} else if (Array.isArray(this.value) && this.value[1]) {
				return `Until ${this.formatDateForDisplay(this.value[1])}`
			}
			return ''
		},
		multiValueArray () {
			if (Array.isArray(this.value)) return this.value
			if (this.value == null || this.value === '') return []
			return [this.value]
		},
		canAdd () {
			const v = (this.newValue || '').toString().trim()
			if (!v) return false
			return !this.multiValueArray.includes(v)
		}
	},
	methods: {
		updateValue (index, newValue) {
			const currentValue = Array.isArray(this.value) ? [...this.value] : [null, null]
			currentValue[index] = newValue
			this.$emit('input', currentValue)
		},
		handleMultiInputChange (vals) {
			// v-combobox emits an array of values for multiple
			const normalized = Array.isArray(vals) ? vals.filter(v => v !== null && v !== undefined).map(v => String(v)) : []
			this.$emit('input', normalized)
		},
		addOneValue () {
			if (!this.canAdd) return
			const next = [...this.multiValueArray, (this.newValue || '').toString().trim()]
			this.newValue = ''
			this.$emit('input', next)
		},
		removeAt (index) {
			const next = [...this.multiValueArray]
			next.splice(index, 1)
			this.$emit('input', next)
		},
		handleDateRangeChange (selectedDates) {
			// selectedDates is an array of selected dates from the range picker
			if (Array.isArray(selectedDates)) {
				let fromDate = selectedDates[0] || null
				let toDate = selectedDates[1] || null

				// Ensure proper date order: from date should be <= to date
				if (fromDate && toDate) {
					const from = new Date(fromDate)
					const to = new Date(toDate)

					// If to date is before from date, swap them
					if (to < from) {
						[fromDate, toDate] = [toDate, fromDate]
					}
				}

				const rangeValue = [fromDate, toDate]
				this.$emit('input', rangeValue)

				// Close the menu when both dates are selected
				if (selectedDates.length === 2) {
					this.$refs.dateRangeMenu.save()
				}
			}
		},
		formatDateForDisplay (dateString) {
			if (!dateString) return ''
			try {
				const date = new Date(dateString)
				if (isNaN(date.getTime())) return dateString
				// Format as a more readable date: Jan 15, 2024
				return date.toLocaleDateString('en-US', {
					year: 'numeric',
					month: 'short',
					day: 'numeric'
				})
			} catch (error) {
				return dateString
			}
		}
	}

}
</script>

<style scoped>
.between-fields-container {
  gap: 12px;
}
.value-chips { display: flex; flex-wrap: wrap; margin-top: 8px; }
.multi-editor > .d-flex { align-items: center; }
/* Remove previous margin-top hack; keep first row inline and chips below */
/* Nudge the input+button row down to align the bottom edge with the operator field */
.multi-editor__input-row { margin-top: 5px; }
</style>
